import { HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { LoadingController } from "@ionic/angular";
import { Observable, catchError, finalize, from, map, of, switchMap, tap, throwError } from "rxjs";

import { DecouplingInfoPlayer } from "@models/I_DecouplingInfoPlayer";
import {
  CasinoSessionsLimits,
  DecoplingLimits,
  DepositsSessionsLimits,
  LimitsChangeResponse,
} from "@models/I_DecouplingLimits";
import { TypeLogout } from "@models/I_DecouplingLogout";
import { I_SelfExclusionRequest } from "@models/I_SelfExclusionDecoupling";
import {
  I_PreValidateSignupArg,
  I_PreValidateSignupResponseArg,
  I_SignupFormArg,
} from "@models/I_SignupArgentinaDecoupling";
import { I_SignupPaForm } from "@models/I_SignupPADecoupling";
import { BaseService } from "./BaseService";
import { I_ComplianceResponseCO, I_SignupCoForm } from "./I_SignupCODecoupling";

import { baseImgPng } from "@constants";
import { DataTables } from "@models/I_CheckDataAvailabilityDecoupling";
import { CommunicationsOptions } from "@models/I_DecouplingCommunications";
import { DecouplingLoginResponse } from "@models/I_DecouplingLogin";
import { PromotableResponse, PromotionOptinDecoupling, PromotionsResponse } from "@models/I_DecouplingPromotions";
import { TimeOutForbiddenActions, TimeOutForm } from "@models/I_DecouplingTimeOut";
import { DataByTokenAndLoginName } from "@models/I_DecouplingTimeRecoverPassword";
import { I_DecouplingTrackersCodereID } from "@models/I_DecouplingTrackersCodereID";
import { DecouplingUserData, RequestUpdateData } from "@models/I_DecouplingUserData";
import { DecouplingRenameKey } from "@models/I_RenameKeyDecoupling";
import { I_SignupCommonDecoupling } from "@models/I_SignupCommonDecoupling";
import { I_SignupFormEs } from "@models/I_SingupEsDecoupling";
import { DecouplingTermsAndConditions } from "@models/I_TermsAndConditionsDecoupling";
import { TrackersCodereId } from "@models/TrackingEvents";
import { DataUserMSOService } from "@mso-services/dataUserMSO.services";
import { UtilsMsoService } from "@mso-services/utilsMso.service";
import { Coords } from "@providers/GeolocationService";

const CODERE_KEY_VALUE = "a9bf68d9-7042-40af-aa28-83a0ea7a9c4f";
const CODERE_KEY = "X-Codere-Key";

enum LoginPlatforms {
  NATIVE = "native",
}

export interface DecouplingResponse<T> {
  value: T | null;
  isSuccess: boolean;
  isFailure: boolean;
  error: ErrorDecoupling;
}

export interface ErrorDecoupling {
  code: string;
  description: string;
  type: number;
  additionalData?: { [key: string]: unknown };
}

export enum ErrorCodes {
  FORBIDDEN = 403,
  UNAUTHORIZED = 401,
  MAINTENACE = 410,
  CONFLICT = 409,
  LOTBA = 19,
}

@Injectable({
  providedIn: "root",
})
export class DecouplingUserServiceMSO extends BaseService {
  dataUserMsoService = inject(DataUserMSOService);
  loadingCtrl = inject(LoadingController);
  loading: HTMLIonLoadingElement;
  loadingCounter = 0;

  public signupPa(data: I_SignupPaForm): Observable<I_SignupCommonDecoupling> {
    return this.customPost<I_SignupCommonDecoupling>(this.globalVars.URL_SIGNUP_DECOUPLING, data, {}, true);
  }

  public signupCo(data: I_SignupCoForm): Observable<I_SignupCommonDecoupling> {
    return this.customPost<I_SignupCommonDecoupling>(this.globalVars.URL_SIGNUP_DECOUPLING, data, {}, true);
  }

  public signupEs(data: I_SignupFormEs): Observable<I_SignupCommonDecoupling> {
    return this.customPost<I_SignupCommonDecoupling>(this.globalVars.URL_SIGNUP_DECOUPLING, data, {}, true);
  }

  public signupArg(data: I_SignupFormArg): Observable<I_SignupCommonDecoupling> {
    return this.customPost<I_SignupCommonDecoupling>(this.globalVars.URL_SIGNUP_DECOUPLING, data, {}, true);
  }

  public preValidateSignupArg(data: I_PreValidateSignupArg): Observable<I_PreValidateSignupResponseArg> {
    return this.customPost<I_PreValidateSignupResponseArg>(
      this.globalVars.URL_PREVALIDATESIGNUP_DECOUPLING,
      data,
      {},
      true,
    );
  }

  public logoutDecoupling(userID: string, type: TypeLogout = TypeLogout.AUTOMATICO) {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customPost<null>(
      this.globalVars.URL_LOGOUT_DECOUPLING,
      { typeLogout: type, playerIdEncrypted: userID },
      { headers },
      true,
      [ErrorCodes.UNAUTHORIZED, ErrorCodes.CONFLICT],
    );
  }

  public refreshCookie() {
    return this.customPost<null>(this.globalVars.URL_REFRESHCOOKIE_DECOUPLING, {}, {}, false, [
      ErrorCodes.UNAUTHORIZED,
    ]);
  }

  public decouplingApplyRequestChangeLimits(accepted: boolean) {
    return this.customPost<null>(this.globalVars.URL_LIMITSAPPLYREQUESTCHANGE_DECOUPLING, { accepted }, {}, true);
  }

  public decouplingCasinoRequestChangeLimits(limits: CasinoSessionsLimits) {
    return this.customPost<LimitsChangeResponse>(
      this.globalVars.URL_LIMITSCASINOREQUESTCHANGE_DECOUPLING,
      limits,
      {},
      true,
    );
  }

  public decouplingDepositsRequestChangeLimits(limits: DepositsSessionsLimits) {
    return this.customPost<LimitsChangeResponse>(
      this.globalVars.URL_LIMITSDEPOSITSREQUESTCHANGE_DECOUPLING,
      limits,
      {},
      true,
    );
  }

  public decouplingGetLimits() {
    return this.customGet<DecoplingLimits>(this.globalVars.URL_GETLIMITS_DECOUPLING, true);
  }

  public decouplingDepositsRequestChangeLimitsWithForm(limits: DepositsSessionsLimits, approbed: boolean) {
    return this.customPost<LimitsChangeResponse>(
      this.globalVars.URL_APPLYLIMITS_DECOUPLING,
      { ...limits, approbed },
      {},
      true,
    );
  }
  public getInfoPlayer() {
    return this.customGet<DecouplingInfoPlayer>(this.globalVars.URL_INFOPLAYER_DECOUPLING, true);
  }

  public selfExclusionDecoupling(data: I_SelfExclusionRequest = {}) {
    return this.customPost<null | { url: string }>(this.globalVars.URL_SELFEXCLUSION_DECOUPLING, data, {}, true);
  }
  public decouplingRegisterCOComplianceRecords() {
    return this.customPost<I_ComplianceResponseCO>(this.globalVars.URL_COMPLIANCE_DECOUPLING, {}, {}, false);
  }

  public getTrackersCodereID(): Observable<TrackersCodereId[]> {
    return this.customGet<I_DecouplingTrackersCodereID[]>(
      this.globalVars.URL_CODEREIDTRACKERS_DECOUPLING,
      false,
    ).pipe(
      map((allTrackers) =>
        allTrackers.map((tracker) => ({
          cantidadapuesta: tracker.betAmount,
          categoriaevento: tracker.category,
          cuota: tracker.quota,
          deporte: tracker.sport,
          evento: tracker.event,
          fechaapuesta: tracker.betDate,
          idapuesta: tracker.betId,
          inplay: tracker.inPlay,
          mercado: tracker.market,
          trackType: tracker.trackerType,
          validUntil: tracker.validUntil,
          value: tracker.code,
        })),
      ),
    );
  }

  public createTrackersCodereID(newTrackers: I_DecouplingTrackersCodereID) {
    return this.customPost<null>(this.globalVars.URL_CODEREIDTRACKERS_DECOUPLING, newTrackers, {}, false);
  }

  public updateTrackersCodereID(newTrackers: I_DecouplingTrackersCodereID) {
    return this.customPut<null>(this.globalVars.URL_CODEREIDTRACKERS_DECOUPLING, newTrackers, {}, false);
  }

  public nativeAutologin(userName: string, encryptedPassword: string, coords: Coords = null) {
    const creds = {
      loginName: userName,
      password: encryptedPassword,
      loginCoordinates: coords,
    };
    return this.customPost<DecouplingLoginResponse>(
      this.globalVars.URL_LOGIN_NATIVE_DECOUPLING,
      creds,
      {},
      true,
    ).pipe(
      map((response) => {
        return {
          ...response,
          lastChangePassword: response.lastChangePassword ? new Date(response.lastChangePassword) : undefined,
          lastLogin: response.lastLogin ? new Date(response.lastLogin) : undefined,
          registrationDate: response.registrationDate ? new Date(response.registrationDate) : undefined,
          modifDateRegulatorFeature: response.modifDateRegulatorFeature
            ? new Date(response.modifDateRegulatorFeature)
            : undefined,
          firstDepositDate: response.firstDepositDate ? new Date(response.firstDepositDate) : undefined,
        };
      }),
    );
  }

  public login(userName: string, password: string, persistCookie?: boolean, coords: Coords = null) {
    const creds = {
      loginName: userName,
      password: password,
      persistCookie: persistCookie,
      deviceType: this.globalVars.isNative ? LoginPlatforms.NATIVE : this.globalVars.PLATFORM,
      __RequestVerificationToken: this.RFToken,
      loginCoordinates: coords,
    };

    return this.customPost<DecouplingLoginResponse>(this.globalVars.URL_LOGIN_DECOUPLING, creds, {}, true).pipe(
      map((response) => {
        return {
          ...response,
          lastChangePassword: response.lastChangePassword ? new Date(response.lastChangePassword) : undefined,
          lastLogin: response.lastLogin ? new Date(response.lastLogin) : undefined,
          registrationDate: response.registrationDate ? new Date(response.registrationDate) : undefined,
          modifDateRegulatorFeature: response.modifDateRegulatorFeature
            ? new Date(response.modifDateRegulatorFeature)
            : undefined,
          firstDepositDate: response.firstDepositDate ? new Date(response.firstDepositDate) : undefined,
        };
      }),
    );
  }

  public autoLoginCookie(coords?: Coords) {
    let url = this.globalVars.URL_LOGIN_DECOUPLING_AUTO;
    if (coords) {
      const { latitude, longitude } = coords;
      url += `?longitude=${longitude}&latitude=${latitude}`;
    }
    return this.customGet<DecouplingLoginResponse>(url, false, [ErrorCodes.UNAUTHORIZED]).pipe(
      map((response) => {
        return {
          ...response,
          lastChangePassword: response.lastChangePassword ? new Date(response.lastChangePassword) : undefined,
          lastLogin: response.lastLogin ? new Date(response.lastLogin) : undefined,
          registrationDate: response.registrationDate ? new Date(response.registrationDate) : undefined,
          modifDateRegulatorFeature: response.modifDateRegulatorFeature
            ? new Date(response.modifDateRegulatorFeature)
            : undefined,
          firstDepositDate: response.firstDepositDate ? new Date(response.firstDepositDate) : undefined,
        };
      }),
    );
  }

  public generateCookie(userId: string) {
    return this.customPost(
      this.globalVars.URL_AUTHORIZE_GENERATE_COOKIE_DECOUPLING,
      { playerUserNameEncrypted: userId },
      {},
    );
  }

  public quickExit() {
    return this.customPost<null>(this.globalVars.URL_QUICKEXIT_DECOUPLING, {}, {}, true);
  }

  public timeOut(data: TimeOutForm) {
    return this.customPost<null>(this.globalVars.URL_TIMEOUTE_DECOUPLING, data, {}, true);
  }

  public timeOutForbiddenActions() {
    return this.customGet<{ products: TimeOutForbiddenActions[] }>(
      this.globalVars.URL_TIMEOUTFORBIDDENACTIONS_DECOUPLING,
      true,
    );
  }

  public getCommunications() {
    return this.customGet<CommunicationsOptions>(this.globalVars.URL_COMMUNICATIONS_DECOUPLING, true);
  }
  public updateCommunications(options: CommunicationsOptions) {
    return this.customPut<null>(this.globalVars.URL_COMMUNICATIONS_DECOUPLING, options, {}, true);
  }

  public updateCommercialCommunications() {
    return this.customPut<null>(this.globalVars.URL_COMERCIALCOMMUNICATION_DECOUPLING, {}, {}, true);
  }

  public reactivateAccount(playerUserName: string, accept = true) {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customPut<null>(
      this.globalVars.URL_REACTIVEACCOUNT_DECOUPLING,
      {
        playerUserName,
        accept,
        deviceType: this.globalVars.isNative ? LoginPlatforms.NATIVE : this.globalVars.PLATFORM,
      },
      { headers },
      true,
    );
  }

  public getUserData() {
    return this.customGet<{ userData: DecouplingUserData }>(this.globalVars.URL_USERDATA_DECOUPLING, true).pipe(
      map(({ userData }) => UtilsMsoService.mapUserDataDecoupling(userData)),
    );
  }

  public updateUserData(dataUpdate: RequestUpdateData) {
    return this.customPut<{ updatedFields: string[] }>(
      this.globalVars.URL_USERDATA_DECOUPLING,
      dataUpdate,
      {},
      true,
    ).pipe(
      tap(() => {
        this.dataUserMsoService.setUserData = UtilsMsoService.mapUserUpdatedData(dataUpdate);
      }),
    );
  }
  public getUserDataByTokenAndLoginName({ token, loginUserName }: { token: string; loginUserName: string }) {
    return this.customGet<DataByTokenAndLoginName>(
      `${this.globalVars.URL_FORGOTPASSWORD_DECOUPLING}?token=${token}&player=${loginUserName}`,
      true,
    );
  }
  public resetPassword(data: { token: string; newPassword: string; playerUserName: string }) {
    return this.customPost<null>(this.globalVars.URL_FORGOTPASSWORD_DECOUPLING, { ...data }, {}, true);
  }

  public sendLinkResetPassword(playerCredential: string) {
    return this.customPost<null>(this.globalVars.URL_LINKRESETPASSWORD_DECOUPLING, { playerCredential }, {}, true);
  }

  public changePassword(data: string) {
    return this.customPost<null>(this.globalVars.URL_CHANGEPASSWORD_DECOUPLING, { data }, {}, true);
  }

  public getPromotionsActives() {
    return this.customGet<PromotionsResponse>(this.globalVars.URL_PROMOTION_ACTIVES_DECOUPLING, true);
  }

  public promotionsOptout(code: string) {
    return this.customPost<{ error: string | null }>(
      this.globalVars.URL_PROMOTIONS_OPTOUT_DECOUPLING,
      { code },
      {},
      true,
    );
  }

  public promotionsOptin(newPromo: PromotionOptinDecoupling) {
    return this.customPost<{ error: string | null }>(
      this.globalVars.URL_PROMOTIONS_OPTIN_DECOUPLING,
      { ...newPromo },
      {},
      true,
    );
  }

  public promotionsCheckOptin(checkPromo: PromotionOptinDecoupling) {
    return this.customPost<{ error: string | null }>(
      this.globalVars.URL_PROMOTIONS_CHECKOPTIN_DECOUPLING,
      { ...checkPromo },
      {},
      true,
    );
  }

  public isPromotable(vip = false) {
    const urlPromotable = vip
      ? this.globalVars.URL_ISPROMOTABLEVIP_DECOUPLING
      : this.globalVars.URL_ISPROMOTABLE_DECOUPLING;
    return this.customGet<PromotableResponse>(urlPromotable, true);
  }

  public getQRCode() {
    return this.customGet<{ qrCode: string }>(this.globalVars.URL_QRCODE_DECOUPLING, true).pipe(
      map((respose) => `${baseImgPng}${respose.qrCode}`),
    );
  }

  public getLastTermsAndConditions() {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customGetOptions<DecouplingTermsAndConditions>(
      this.globalVars.URL_TERMSANDCONDITIONS_DECOUPLING,
      { headers },
      true,
    );
  }

  public updateTermsAndConditions(userName: string) {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customPut<null>(
      this.globalVars.URL_TERMSANDCONDITIONS_DECOUPLING,
      { loginName: userName },
      { headers },
      true,
    );
  }

  public getLastTermsAndConditionsSelfie() {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customGetOptions<DecouplingTermsAndConditions>(
      this.globalVars.URL_TERMSANDCONDITIONS_SELFIE_DECOUPLING,
      { headers },
      true,
    );
  }

  public updateLastTermsAndConditionsSelfie(userName: string) {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customPut<null>(
      this.globalVars.URL_TERMSANDCONDITIONS_SELFIE_DECOUPLING,
      { loginName: userName },
      { headers },
      true,
    );
  }

  public getLastPrivacyPolitics() {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customGetOptions<DecouplingTermsAndConditions>(
      this.globalVars.URL_PRIVACYPOLICY_DECOUPLING,
      { headers },
      true,
    );
  }

  public updatePrivacyPolitics(userName: string) {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customPut<null>(
      this.globalVars.URL_PRIVACYPOLICY_DECOUPLING,
      { loginName: userName },
      { headers },
      true,
    );
  }

  public getAuthorizationPersonalData() {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customGetOptions<DecouplingTermsAndConditions>(
      this.globalVars.URL_AUTHORIZATIONPERSONALDATA_DECOUPLING,
      { headers },
      true,
    );
  }

  public updateAuthorizationPersonalData(userName: string) {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customPut<null>(
      this.globalVars.URL_AUTHORIZATIONPERSONALDATA_DECOUPLING,
      { loginName: userName },
      { headers },
      true,
    );
  }

  public renameKey(data: DecouplingRenameKey) {
    return this.customPost<null>(this.globalVars.URL_RENAMEKEY_DECOUPLING, data, {}, false);
  }

  public checkDataAvailability(table: DataTables, value: string) {
    const url = this.globalVars.URL_CHECKDATAAVAILABILITY_DECOUPLING.replace("{1}", table).replace("{2}", value);
    return this.customGet<{ exists: boolean }>(url, false);
  }

  public postSignup(contactId: string) {
    const headers = new HttpHeaders().append(CODERE_KEY, CODERE_KEY_VALUE);
    return this.customPost<null>(this.globalVars.URL_POSTSIGNUP_DECOUPLING, { contactId }, { headers }, false);
  }

  private get RFToken(): string {
    try {
      const token: HTMLInputElement = <HTMLInputElement>(
        document.getElementsByName("__RequestVerificationToken")[0]
      );
      return token.value;
    } catch (_) {
      return "";
    }
  }

  private customHandleError(
    skiptDefaultError: ErrorCodes[] = [],
  ): (error: HttpErrorResponse) => Observable<never> {
    return (errorResponse: HttpErrorResponse): Observable<never> => {
      const { status, error } = errorResponse;

      if (!skiptDefaultError.includes(status)) {
        switch (status) {
          case ErrorCodes.UNAUTHORIZED:
          case ErrorCodes.FORBIDDEN:
            this.events.publish("user:logout:soft");
            break;
          case ErrorCodes.MAINTENACE:
            this.events.publish("user:serverfailmaintenace", error);
            break;
        }
      }

      const newErr: ErrorDecoupling = {
        code: error?.error?.code || "",
        type: error?.error?.type || status,
        description: error?.error?.description || "",
        additionalData: error?.error?.additionalData,
      };

      return throwError(() => newErr);
    };
  }

  private customPipe<T>(observable: Observable<T>, loading = false): Observable<T> {
    const loaderObservable = loading ? from(this.showLoader()).pipe(switchMap(() => of(null))) : of(null);

    return loaderObservable.pipe(
      switchMap(() =>
        observable.pipe(
          map((response) => {
            const data = response as DecouplingResponse<T>;

            if (data.isSuccess === false) {
              throwError(() => data.error);
            } else {
              return data.value || null;
            }
          }),
          finalize(async () => {
            if (loading) {
              await this.hideLoader();
            }
          }),
        ),
      ),
    );
  }

  protected customGetOptions<T>(
    url: string,
    options: unknown,
    loading = false,
    skiptDefaultError: ErrorCodes[] = [],
  ): Observable<T> {
    return this.customPipe<T>(this.httpClient.get<T>(url, options), loading).pipe(
      catchError(this.customHandleError(skiptDefaultError)),
    );
  }
  protected customGet<T>(url: string, loading = false, skiptDefaultError: ErrorCodes[] = []): Observable<T> {
    return this.customPipe<T>(this.myGet(url, false, this.customHandleError(skiptDefaultError)), loading);
  }
  protected customPost<T>(
    url: string,
    data: any,
    options: any,
    loading = false,
    skiptDefaultError: ErrorCodes[] = [],
  ): Observable<T> {
    return this.customPipe<T>(
      this.myPost(url, data, options, false, this.customHandleError(skiptDefaultError)),
      loading,
    );
  }
  protected customPut<T>(
    url: string,
    data: any,
    options: any,
    loading = false,
    skiptDefaultError: ErrorCodes[] = [],
  ): Observable<T> {
    return this.customPipe<T>(
      this.myPut(url, data, options, false, this.customHandleError(skiptDefaultError)),
      loading,
    );
  }
  protected customDelete<T>(url: string, loading = false, skiptDefaultError: ErrorCodes[] = []): Observable<T> {
    return this.customPipe<T>(this.myDelete(url, false, this.customHandleError(skiptDefaultError)), loading);
  }

  private async showLoader() {
    if (this.loadingCounter === 0) {
      this.loadingCounter++;
      this.loading = await this.loadingCtrl.create({
        spinner: "crescent",
        backdropDismiss: false,
        cssClass: "is-loader check",
        translucent: true,
      });
      await this.loading.present();
    } else {
      this.loadingCounter++;
    }
  }

  private async hideLoader() {
    this.loadingCounter--;
    if (this.loadingCounter <= 0) {
      this.loadingCounter = 0;
      await this.loading.dismiss();
    }
  }
}
